// SEC2-S3 (docs/build-briefs/SEC2-device-setup-code-binding.md section 4.7, DEC-320, DEC-321): the view model
// behind Settings > Devices (the file keeps its tablets name, UX84). Pure (no DOM, no fetch), so test/ui-tablets-model.test.ts imports it directly.
// ui/src/tablets.ts draws what this returns, setting every piece of text with textContent: a device's name is
// typed by a person and must never be read as markup (the S1 review's carry-forward on user-agent text applies
// the same rule; this screen shows no user-agent at all).

import { sortByName } from "./nameSort.ts";

/** Mirrors src/domain/deviceBinding.ts DeviceRecord (GET /tenants/:id/devices). */
export interface DeviceDto {
  id: string;
  locationId: string;
  displayName: string;
  createdAt: string;
  lastSeenAt: string | null;
  revokedAt: string | null;
  state: "active" | "revoked";
}

/** Mirrors DeviceSetupCodeRecord (GET /tenants/:id/device-setup-codes). The code itself is never in it. */
export interface SetupCodeDto {
  id: string;
  locationId: string;
  displayName: string;
  expiresAt: string;
  createdAt: string;
  state: "waiting" | "redeemed" | "revoked" | "expired";
  redeemedDeviceId: string | null;
}

/** Mirrors TabletsScreenLocation: the locations a manager may pick, and which one is the main location. */
export interface LocationDto {
  id: string;
  name: string;
  isMain: boolean;
}

/** Mirrors src/domain/deviceBinding.ts SETUP_CODE_TTL_MINUTES and MAX_OUTSTANDING_SETUP_CODES. */
export const CODE_LIFETIME_MS = 15 * 60_000;
export const MAX_WAITING_CODES = 5;
/** The countdown turns orange under two minutes (mockup 9b). */
export const LOW_TIME_MS = 2 * 60_000;
export const MAX_NAME_LENGTH = 80;

export const TABLET_NAME_PRESETS: readonly string[] = [
  "Front bar device",
  "Back bar device",
  "Patio device",
  "Spare device",
];
export const OTHER_NAME = "Other name";
/** UX89: the name a code carries when the manager leaves naming to the device. The device is asked for its own name
 * right after it accepts the code (ui/src/terminalSetup.ts), so this is a placeholder, not a choice. */
export const DEFAULT_DEVICE_NAME = "New device";

const LOCATION_NOT_LISTED = "A location that is no longer listed";

// ---------------------------------------------------------------------------------------------------------
// Time

/** m:ss, rounding a part second UP so the screen never reads 0:00 while the code still works. */
export function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export interface Countdown {
  readonly remainingMs: number;
  readonly text: string;
  /** 1 at the start of a code's life, 0 at its end (the location under the code). */
  readonly fraction: number;
  readonly low: boolean;
  readonly expired: boolean;
}

export function codeCountdown(code: Pick<SetupCodeDto, "expiresAt">, nowMs: number): Countdown {
  const remainingMs = Math.max(0, Date.parse(code.expiresAt) - nowMs);
  const expired = remainingMs === 0;
  return {
    remainingMs,
    text: formatCountdown(remainingMs),
    fraction: Math.min(1, remainingMs / CODE_LIFETIME_MS),
    low: !expired && remainingMs < LOW_TIME_MS,
    expired,
  };
}

export interface TimeOptions {
  /** An IANA zone. Unset means the browser's own zone. */
  readonly timeZone?: string;
}

function dayKey(ms: number, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(timeZone === undefined ? {} : { timeZone }),
  }).format(ms);
}

function clock(ms: number, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone === undefined ? {} : { timeZone }),
  })
    .format(ms)
    .replace(/[\u202f\u00a0]/g, " ");
}

/** "Last used today, 4:12 PM", "Last used yesterday, 11:40 PM", "Last used Fri, Oct 2, 9:30 AM", or "Not used yet". */
export function formatLastUsed(
  iso: string | null,
  nowMs: number,
  options: TimeOptions = {},
): string {
  if (iso === null) return "Not used yet";
  const at = Date.parse(iso);
  const zone = options.timeZone;
  const time = clock(at, zone);
  const day = dayKey(at, zone);
  if (day === dayKey(nowMs, zone)) return `Last used today, ${time}`;
  if (day === dayKey(nowMs - 86_400_000, zone)) return `Last used yesterday, ${time}`;
  const date = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(zone === undefined ? {} : { timeZone: zone }),
  }).format(at);
  return `Last used ${date}, ${time}`;
}

// ---------------------------------------------------------------------------------------------------------
// The list

function locationLabel(locationId: string, locations: readonly LocationDto[]): string {
  return locations.find((location) => location.id === locationId)?.name ?? LOCATION_NOT_LISTED;
}

export interface TabletRow {
  readonly id: string;
  readonly name: string;
  readonly locationName: string;
  readonly lastUsed: string;
  /** The small tag beside the name. */
  readonly tag: string;
  readonly tone: "good" | "neutral" | "revoked";
}

export interface TabletRows {
  readonly active: readonly TabletRow[];
  readonly revoked: readonly TabletRow[];
}

export function buildTabletRows(
  devices: readonly DeviceDto[],
  locations: readonly LocationDto[],
  nowMs: number,
  options: TimeOptions = {},
): TabletRows {
  const active: TabletRow[] = [];
  const revoked: TabletRow[] = [];
  for (const device of devices) {
    const base = {
      id: device.id,
      name: device.displayName,
      locationName: locationLabel(device.locationId, locations),
      lastUsed: formatLastUsed(device.lastSeenAt, nowMs, options),
    };
    if (device.state === "revoked") {
      revoked.push({ ...base, tag: "Revoked", tone: "revoked" });
    } else if (device.lastSeenAt === null) {
      active.push({ ...base, tag: "Set up", tone: "neutral" });
    } else {
      active.push({ ...base, tag: "Working", tone: "good" });
    }
  }
  // UX88 (DEC-339): devices read A to Z by name.
  return {
    active: sortByName(active, (row) => row.name),
    revoked: sortByName(revoked, (row) => row.name),
  };
}

// ---------------------------------------------------------------------------------------------------------
// Codes waiting to be used

export interface WaitingCodeRow {
  readonly id: string;
  readonly name: string;
  readonly locationName: string;
  readonly timeLeft: string;
  readonly fraction: number;
  readonly low: boolean;
}

export interface WaitingCodes {
  readonly rows: readonly WaitingCodeRow[];
  readonly count: number;
  /** Always five entries, true for each code waiting. */
  readonly pips: readonly boolean[];
  readonly atLimit: boolean;
}

export function buildWaitingCodes(
  codes: readonly SetupCodeDto[],
  locations: readonly LocationDto[],
  nowMs: number,
): WaitingCodes {
  const waiting = codes
    .filter((code) => code.state === "waiting" && Date.parse(code.expiresAt) > nowMs)
    .sort((a, b) => Date.parse(a.expiresAt) - Date.parse(b.expiresAt));
  const rows = waiting.map((code): WaitingCodeRow => {
    const countdown = codeCountdown(code, nowMs);
    return {
      id: code.id,
      name: code.displayName,
      locationName: locationLabel(code.locationId, locations),
      timeLeft: countdown.text,
      fraction: countdown.fraction,
      low: countdown.low,
    };
  });
  const pips: boolean[] = [];
  for (let index = 0; index < MAX_WAITING_CODES; index += 1) pips.push(index < rows.length);
  return { rows, count: rows.length, pips, atLimit: rows.length >= MAX_WAITING_CODES };
}

// ---------------------------------------------------------------------------------------------------------
// The Add a device form: no default location; the name is optional (UX89) and defaults to leaving it to the device

export interface AddFormState {
  /** The location tapped by the manager. Never pre-set when there is a choice. */
  readonly locationId: string | undefined;
  /** A chip's text, or OTHER_NAME. Never pre-set. */
  readonly namePreset: string | undefined;
  readonly customName: string;
}

export function initialAddForm(): AddFormState {
  return { locationId: undefined, namePreset: undefined, customName: "" };
}

/** The location this device would be tied to. DEVSIMPLE1: a business with exactly one location has it chosen for
 * the manager; with two or more only the tapped one counts. Undefined when there is none, never a guess. */
export function chosenLocationId(
  state: AddFormState,
  locations: readonly LocationDto[],
): string | undefined {
  if (locations.length === 1) return locations[0]?.id;
  return locations.find((location) => location.id === state.locationId)?.id;
}

/** The name the code is made with. UX89: the name is optional. No chip chosen, or "Other name" with nothing typed,
 * is DEFAULT_DEVICE_NAME and the device names itself. A typed name past the limit is undefined (never silently
 * cut), which keeps Make code off until it is fixed. */
export function chosenName(state: AddFormState): string | undefined {
  if (state.namePreset === undefined) return DEFAULT_DEVICE_NAME;
  if (state.namePreset !== OTHER_NAME) return state.namePreset;
  const typed = state.customName.trim();
  if (typed.length === 0) return DEFAULT_DEVICE_NAME;
  return typed.length <= MAX_NAME_LENGTH ? typed : undefined;
}

/** True when the manager is leaving the naming to the device. */
export function leavesNamingToDevice(state: AddFormState): boolean {
  return chosenName(state) === DEFAULT_DEVICE_NAME && state.namePreset !== DEFAULT_DEVICE_NAME;
}

export function canMakeCode(state: AddFormState, locations: readonly LocationDto[]): boolean {
  return chosenLocationId(state, locations) !== undefined && chosenName(state) !== undefined;
}

// ---------------------------------------------------------------------------------------------------------
// Refusals of "Make code"

export interface IssueFailureView {
  readonly kind: "limit" | "other";
  readonly title: string;
  readonly headline?: string;
  readonly detail: string;
}

export function issueFailureView(
  status: number,
  body: { error?: string; message?: string } | undefined,
  networkError: boolean,
): IssueFailureView {
  if (networkError || status === 0) {
    return {
      kind: "other",
      title: "Could not make a code",
      detail: "Could not reach the server. Check your connection and try again.",
    };
  }
  if (status === 409 && body?.error === "setup_code_limit") {
    return {
      kind: "limit",
      title: `${MAX_WAITING_CODES} codes are already waiting`,
      headline: "You cannot make a sixth code yet.",
      detail: "Cancel a code you do not need, or wait for one to end.",
    };
  }
  return {
    kind: "other",
    title: "Could not make a code",
    detail: body?.message ?? "Something went wrong. Try again.",
  };
}
